import { completeRecoveryUnlock } from '../crypto/recovery';
import { resolvePasswordPolicy } from '../crypto/passwordPoliciesApi';
import { defaultPolicyForTarget } from '../crypto/passwordPolicy';
import { seedInfoReplyKeysFromVault } from '../crypto/vaultPersist';
import { storeVaultSession } from '../crypto/vaultSession';
import { markVaultUnlocked } from '../app';
import { applyI18n, t } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { dialogHelpTopic } from '../help/dialogHelpTopics';
import { setCryptoBusy } from './cryptoBusy';
import { iconImg } from './icons';
import { bindModalDismiss, createModalShell } from './modalDialogShell';
import { attachPassphraseWhitespaceWarnings } from './passphraseWhitespaceWarning';
import {
  bindPasswordPolicyToForm,
  passwordPolicyErrorFromCause,
  validatePasswordForUi,
} from './passwordPolicyUi';

function recoveryErrorKey(cause: unknown): string {
  if (cause instanceof Error) {
    switch (cause.message) {
      case 'recovery_not_active':
        return 'vault.recovery.notActive';
      case 'recovery_generation_mismatch':
      case 'recovery_public_key_mismatch':
      case 'recovery_user_mismatch':
      case 'invalid_recovery_export':
        return 'vault.recovery.invalidExport';
      case 'vault_version_mismatch':
        return 'vault.unlock.versionMismatch';
      case 'vault_missing_user_priv':
        return 'vault.unlock.missingUserPriv';
      case 'vault_passphrase_invalid':
        return 'vault.unlock.passphraseInvalid';
      default:
        break;
    }
  }
  return 'status.error';
}

export function openVaultRecoveryDialog(userId: string): void {
  const shell = createModalShell({
    titleKey: 'vault.recovery.title',
    titleId: 'vault-recovery-dialog-title',
    helpTopic: dialogHelpTopic('vaultRecovery'),
    className: 'vault-recovery-dialog',
  });

  shell.body.innerHTML = `
    <p class="muted" data-i18n="vault.recovery.subtitle"></p>
    <div class="warning-panel">
      <span class="warning-panel__icon"></span>
      <span data-i18n="vault.recovery.sensitivityWarning"></span>
    </div>
    <form class="form-grid" id="vault-recovery-form" method="post" action="#">
      <div class="recovery-import-field">
        <label class="message-field">
          <span data-i18n="vault.recovery.exportLabel"></span>
          <textarea class="message-input" name="exportText" required></textarea>
        </label>
        <div class="recovery-import-file">
          <input
            type="file"
            id="vault-recovery-file"
            accept=".txt,.text,text/plain"
            hidden
          />
          <button type="button" class="button ghost" id="vault-recovery-choose-file">
            <span data-i18n="vault.recovery.chooseFile"></span>
          </button>
          <span class="muted recovery-import-file__name" id="vault-recovery-file-name" hidden></span>
        </div>
      </div>
      <label>
        <span data-i18n="vault.recovery.newPassphrase"></span>
        <input type="password" name="passphrase" minlength="12" required autocomplete="new-password" />
      </label>
      <label>
        <span data-i18n="vault.recovery.confirmPassphrase"></span>
        <input type="password" name="confirm" minlength="12" required autocomplete="new-password" />
      </label>
      <p class="muted" id="vault-recovery-policy-hint" hidden></p>
    </form>
    <p class="error-text" id="vault-recovery-error" hidden></p>
  `;

  const cancelButton = document.createElement('button');
  cancelButton.type = 'button';
  cancelButton.className = 'button ghost';
  cancelButton.id = 'vault-recovery-cancel';
  cancelButton.innerHTML = '<span data-i18n="common.cancel"></span>';

  const submitButton = document.createElement('button');
  submitButton.type = 'submit';
  submitButton.className = 'button primary';
  submitButton.setAttribute('form', 'vault-recovery-form');
  submitButton.innerHTML = '<span data-i18n="vault.recovery.submit"></span>';

  shell.actions.append(cancelButton, submitButton);
  document.body.append(shell.backdrop);
  shell.body.querySelector('.warning-panel__icon')?.append(iconImg('warning', 20));
  applyI18n(shell.dialog);

  const form = shell.body.querySelector('#vault-recovery-form') as HTMLFormElement;
  const error = shell.body.querySelector('#vault-recovery-error') as HTMLParagraphElement;
  const policyHint = shell.body.querySelector('#vault-recovery-policy-hint') as HTMLElement;
  const passphraseInput = form.querySelector('input[name="passphrase"]') as HTMLInputElement;
  const confirmInput = form.querySelector('input[name="confirm"]') as HTMLInputElement;
  let recoveryPolicy = defaultPolicyForTarget('vaultPassphrase');
  void resolvePasswordPolicy('vaultPassphrase').then((policy) => {
    recoveryPolicy = policy;
    bindPasswordPolicyToForm({
      policy,
      inputs: [passphraseInput, confirmInput],
      hint: policyHint,
    });
  });
  attachPassphraseWhitespaceWarnings(passphraseInput, confirmInput);
  const exportField = form.querySelector('textarea[name="exportText"]') as HTMLTextAreaElement;
  const fileInput = shell.body.querySelector('#vault-recovery-file') as HTMLInputElement;
  const fileNameEl = shell.body.querySelector('#vault-recovery-file-name') as HTMLSpanElement;
  let errorKey: string | null = null;

  async function loadRecoveryFile(file: File): Promise<void> {
    error.hidden = true;
    errorKey = null;

    try {
      const text = (await file.text()).trim();
      if (!text) {
        errorKey = 'vault.recovery.fileEmpty';
        error.hidden = false;
        error.textContent = t(errorKey);
        return;
      }

      exportField.value = text;
      fileNameEl.dataset.fileName = file.name;
      fileNameEl.textContent = t('vault.recovery.fileSelected', { name: file.name });
      fileNameEl.hidden = false;
    } catch (cause) {
      console.error('Read recovery file failed', cause);
      errorKey = 'vault.recovery.fileReadFailed';
      error.hidden = false;
      error.textContent = t(errorKey);
    }
  }

  shell.body.querySelector('#vault-recovery-choose-file')?.addEventListener('click', () => {
    fileInput.click();
  });

  fileInput.addEventListener('change', () => {
    const file = fileInput.files?.[0];
    if (!file) {
      return;
    }

    void loadRecoveryFile(file);
    fileInput.value = '';
  });

  const close = () => {
    unbindLocale();
    unbindDismiss();
    shell.backdrop.remove();
  };

  const unbindLocale = bindLocaleRefresh(() => {
    if (errorKey && !error.hidden) {
      error.textContent = t(errorKey);
    }
    if (!fileNameEl.hidden && fileNameEl.dataset.fileName) {
      fileNameEl.textContent = t('vault.recovery.fileSelected', { name: fileNameEl.dataset.fileName });
    }
    applyI18n(shell.dialog);
  });

  const unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: close,
  });

  cancelButton.addEventListener('click', close);

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      error.hidden = true;
      errorKey = null;
      const data = new FormData(form);
      const exportText = String(data.get('exportText') ?? '').trim();
      const passphrase = String(data.get('passphrase') ?? '');
      const confirm = String(data.get('confirm') ?? '');

      if (passphrase !== confirm) {
        errorKey = 'vault.setup.mismatch';
        error.hidden = false;
        error.textContent = t(errorKey);
        return;
      }

      const policyMessage = validatePasswordForUi(passphrase, recoveryPolicy);
      if (policyMessage) {
        errorKey = null;
        error.hidden = false;
        error.textContent = policyMessage;
        return;
      }

      setCryptoBusy(shell.dialog, form, true, {
        titleKey: 'vault.recovery.working',
        detailKey: 'vault.recovery.workingDetail',
        icon: 'hourglass',
      });

      try {
        const recovered = await completeRecoveryUnlock(userId, exportText, passphrase);
        storeVaultSession(recovered.vaultKey, recovered.userPrivateKey);
        seedInfoReplyKeysFromVault(recovered.plaintext);
        markVaultUnlocked();
        close();
      } catch (cause) {
        console.error('Vault recovery unlock failed', cause);
        const policyError = passwordPolicyErrorFromCause(cause, recoveryPolicy);
        if (policyError) {
          errorKey = null;
          error.hidden = false;
          error.textContent = policyError;
        } else {
          errorKey = recoveryErrorKey(cause);
          error.hidden = false;
          error.textContent = t(errorKey);
        }
      } finally {
        setCryptoBusy(shell.dialog, form, false, {
          titleKey: 'vault.recovery.working',
          detailKey: 'vault.recovery.workingDetail',
        });
      }
    })();
  });

  exportField.focus();
}
